/**
 * `cameraPresets.ts` — WP-6 (3D model / BIM / 3D Tiles ToolCard).
 *
 * Pure helpers for the camera-preset slice extracted from the legacy
 * `Giro3DToolbar.tsx` (2025 KB monolith) so the right-rail
 * `<ThreeDModelToolCard />` and the in-canvas overlay both call the same
 * math. Keeps Three.js-specific work behind the
 * {@link Giro3DCameraTarget} abstraction so the helper can be unit-tested
 * without instantiating a Three.js scene.
 *
 * Spec references:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
 *     §7.6 Card 6 (camera presets: Top, Front, Right, Iso, Fit, plus
 *     Back / Left for the full Navisworks/Cesium parity set);
 *   - `geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md`
 *     (icons paired with each preset live in the ToolCard, not here).
 */

export type CameraPreset =
  | 'top'
  | 'front'
  | 'back'
  | 'right'
  | 'left'
  | 'iso'
  | 'fit';

/**
 * Stable ordered list of presets the right-rail card surfaces. Tests
 * assert this order so the card's icon row never silently re-arranges
 * (which would break user muscle memory).
 */
export const CAMERA_PRESETS: readonly CameraPreset[] = [
  'top',
  'front',
  'back',
  'right',
  'left',
  'iso',
  'fit',
] as const;

/** Bounds returned by `Giro3DAdapter.getModelBounds()`. */
export interface ModelBounds {
  readonly center: readonly [number, number, number];
  readonly size: readonly [number, number, number];
  readonly diagonal: number;
}

/**
 * Derived camera target for a preset. The numbers are world-space
 * positions; consumers translate them into Three.js calls. Pure so this
 * helper has no engine import surface.
 */
export interface CameraPresetTarget {
  readonly position: readonly [number, number, number];
  readonly target: readonly [number, number, number];
  readonly up: readonly [number, number, number];
  readonly near: number;
  readonly far: number;
}

/** Multiplier used to keep the camera outside the bounding sphere. */
const FRAMING_DISTANCE_MULTIPLIER = 1.5;
/** Iso framing pulls the camera to a 0.5 / 0.5 / 0.6 unit cube vertex. */
const ISO_OFFSETS: readonly [number, number, number] = [0.5, -0.5, 0.6];
/** Fit framing keeps each axis at 0.6 so the bounding box clears all sides. */
const FIT_OFFSETS: readonly [number, number, number] = [0.6, 0.6, 0.6];
/** Initial near plane scale before the per-frame log-depth controller refines it. */
const INITIAL_NEAR_SCALE = 0.0005;
/** Initial far plane scale; adapter's `updateCameraClipping` widens this every frame. */
const INITIAL_FAR_MULTIPLIER = 20;
/** Minimum near plane returned for tiny models so the projection never collapses. */
const MIN_NEAR_PLANE = 0.01;

/**
 * Compute the camera target for a preset given the current model bounds.
 * Returns `null` when no model is loaded — consumers must short-circuit
 * (the right-rail card disables every preset chip in that state).
 */
export function computeCameraPresetTarget(
  preset: CameraPreset,
  bounds: ModelBounds | null,
): CameraPresetTarget | null {
  if (!bounds) return null;

  const [cx, cy, cz] = bounds.center;
  const distance = bounds.diagonal * FRAMING_DISTANCE_MULTIPLIER;
  const near = Math.max(MIN_NEAR_PLANE, bounds.diagonal * INITIAL_NEAR_SCALE);
  const far = Math.max(distance * INITIAL_FAR_MULTIPLIER, 1000);

  switch (preset) {
    case 'top':
      return {
        position: [cx, cy, cz + distance],
        target: [cx, cy, cz],
        up: [0, 1, 0],
        near,
        far,
      };
    case 'front':
      return {
        position: [cx, cy - distance, cz],
        target: [cx, cy, cz],
        up: [0, 0, 1],
        near,
        far,
      };
    case 'back':
      return {
        position: [cx, cy + distance, cz],
        target: [cx, cy, cz],
        up: [0, 0, 1],
        near,
        far,
      };
    case 'right':
      return {
        position: [cx + distance, cy, cz],
        target: [cx, cy, cz],
        up: [0, 0, 1],
        near,
        far,
      };
    case 'left':
      return {
        position: [cx - distance, cy, cz],
        target: [cx, cy, cz],
        up: [0, 0, 1],
        near,
        far,
      };
    case 'iso':
      return {
        position: [
          cx + distance * ISO_OFFSETS[0],
          cy + distance * ISO_OFFSETS[1],
          cz + distance * ISO_OFFSETS[2],
        ],
        target: [cx, cy, cz],
        up: [0, 0, 1],
        near,
        far,
      };
    case 'fit':
      return {
        position: [
          cx + distance * FIT_OFFSETS[0],
          cy + distance * FIT_OFFSETS[1],
          cz + distance * FIT_OFFSETS[2],
        ],
        target: [cx, cy, cz],
        up: [0, 0, 1],
        near,
        far,
      };
    default: {
      const exhaustive: never = preset;
      void exhaustive;
      return null;
    }
  }
}

/**
 * Minimal Three.js-shaped camera surface the adapter passes in. Modelled
 * as `unknown`-typed records so this module has zero `import 'three'`
 * line — tests run without bundling Three.
 */
export interface Giro3DCameraTarget {
  readonly camera: {
    position: { set(x: number, y: number, z: number): void };
    up: { set(x: number, y: number, z: number): void };
    lookAt(x: number, y: number, z: number): void;
    near: number;
    far: number;
    updateProjectionMatrix?: () => void;
  } | null;
  readonly controls?: {
    target: { set(x: number, y: number, z: number): void };
    update?: () => void;
  } | null;
  notifyChange?: () => void;
}

/**
 * Apply a preset target to a live camera + orbit-controls surface. Returns
 * `true` when applied, `false` when there was nothing to act on (used by
 * the adapter to surface a no-op debug log without throwing).
 */
export function applyCameraPresetTarget(
  surface: Giro3DCameraTarget | null,
  target: CameraPresetTarget | null,
): boolean {
  if (!surface || !target) return false;
  const cam = surface.camera;
  if (!cam) return false;

  const [px, py, pz] = target.position;
  const [tx, ty, tz] = target.target;
  const [ux, uy, uz] = target.up;

  cam.position.set(px, py, pz);
  cam.up.set(ux, uy, uz);
  cam.lookAt(tx, ty, tz);
  cam.near = target.near;
  cam.far = target.far;
  cam.updateProjectionMatrix?.();

  const controls = surface.controls;
  if (controls?.target) {
    controls.target.set(tx, ty, tz);
    controls.update?.();
  }

  surface.notifyChange?.();
  return true;
}

/**
 * Human-readable label for a preset (used by the ToolCard's chip
 * accessible names). Kept here so the UI never invents its own labels.
 */
export function cameraPresetLabel(preset: CameraPreset): string {
  switch (preset) {
    case 'top':
      return 'Top';
    case 'front':
      return 'Front';
    case 'back':
      return 'Back';
    case 'right':
      return 'Right';
    case 'left':
      return 'Left';
    case 'iso':
      return 'Isometric';
    case 'fit':
      return 'Fit to model';
  }
}
